import { Eye } from 'lucide-react';
import { Link } from 'react-router';
import type { CreditAnalysis, PaginatedResponse } from '@/api/commercial/credit-analysis';
import { CreditAnalysisStatus } from '@/api/commercial/credit-analysis';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  CREDIT_ANALYSIS_SITUATION_LABEL,
  CREDIT_ANALYSIS_STATUS_LABEL,
  type CreditAnalysisSituation,
} from '@/lib/constants/credit-analysis';

interface CreditAnalysisTableProps {
  data: PaginatedResponse<CreditAnalysis>;
  currentPage: number;
  onPageChange: (page: number) => void;
}

function getSituation(status: CreditAnalysisStatus): CreditAnalysisSituation | '-' {
  switch (status) {
    case CreditAnalysisStatus.APPROVED:
      return 'APPROVED';
    case CreditAnalysisStatus.PARTIALLY_APPROVED:
      return 'PARTIALLY_APPROVED';
    case CreditAnalysisStatus.DECLINED:
    case CreditAnalysisStatus.DOC_DECLINED:
      return 'DECLINED';
    default:
      return '-';
  }
}

export function CreditAnalysisTable({ data, currentPage, onPageChange }: CreditAnalysisTableProps) {
  const formatDate = (value?: string | Date): string => {
    if (!value) return '-';
    const date = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatNextAnalysis = (value?: string | 'NEXT_PURCHASE'): string => {
    if (!value) return '-';
    if (value === 'NEXT_PURCHASE') return 'Próxima compra';
    return formatDate(value);
  };

  const docs = data.docs ?? [];

  return (
    <div className="w-full space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>CLIENTE</TableHead>
              <TableHead>SOLICITADO EM</TableHead>
              <TableHead>ANALISADO EM</TableHead>
              <TableHead>PRÓXIMA ANÁLISE</TableHead>
              <TableHead>SITUAÇÃO ATUAL</TableHead>
              <TableHead>STATUS</TableHead>
              <TableHead className="text-left">AÇÃO</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  Nenhuma análise de crédito encontrada
                </TableCell>
              </TableRow>
            ) : (
              docs.map((analysis) => {
                const status = CREDIT_ANALYSIS_STATUS_LABEL[analysis.status] || {
                  label: analysis.status,
                  color: 'bg-gray-100 text-gray-800',
                };
                const situation = getSituation(analysis.status);
                const situationStyle = CREDIT_ANALYSIS_SITUATION_LABEL[situation];

                return (
                  <TableRow key={analysis.id}>
                    <TableCell>
                      <span className="font-medium">{analysis.clientName || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDate(analysis.created_at)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDate(analysis.analyzed_at)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatNextAnalysis(analysis.newAnalysisIn)}</span>
                    </TableCell>
                    <TableCell>
                      {situation !== '-' ? (
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${situationStyle.color}`}
                        >
                          {situationStyle.label}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status.color}`}
                      >
                        {status.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-left">
                      <Link to={`/commercial/credit-analysis/${analysis.id}`}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          title="Visualizar"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={data.pages}
        totalItems={data.total}
        limit={data.limit}
        onPageChange={onPageChange}
      />
    </div>
  );
}
